import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { AppConfig } from "@src/config/AppConfig";
import { GetMasterDataDeductionTypesDTO } from "@src/domain/dto/masterData";
import type { MasterDataDeductionType } from "@src/domain/entities/masterData";
import { GetMasterDataDeductionTypesUseCase } from "@src/application/useCases/masterData";
import { MasterDataDeductionTypeRepository } from "@src/infrastructure/repositories/masterData";
import { useMasterDataListQuery } from "./masterData/useMasterDataListQuery";

interface MasterDataDeductionTypesDataContextValue {
  brandKey: string;
  deductionTypes: MasterDataDeductionType[];
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (search: string) => void;
  page: number;
  limit: number;
  total: number;
  setPage: (page: number) => void;
  refetch: () => void;
}

const MasterDataDeductionTypesDataContext =
  createContext<MasterDataDeductionTypesDataContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useMasterDataDeductionTypes(): MasterDataDeductionTypesDataContextValue {
  const context = useContext(MasterDataDeductionTypesDataContext);
  if (!context) {
    throw new Error(
      "useMasterDataDeductionTypes must be used within MasterDataDeductionTypesDataProvider",
    );
  }
  return context;
}

interface MasterDataDeductionTypesDataProviderProps {
  brandKey: string;
  children: ReactNode;
  defaultPageSize?: number;
}

export function MasterDataDeductionTypesDataProvider({
  brandKey,
  children,
  defaultPageSize = AppConfig.getDefaultPaginationLimit(),
}: Readonly<MasterDataDeductionTypesDataProviderProps>) {
  const getDeductionTypesUseCase = useMemo(
    () =>
      new GetMasterDataDeductionTypesUseCase(
        new MasterDataDeductionTypeRepository(),
      ),
    [],
  );

  const fetchPage = useCallback(
    async (args: { search: string | null; page: number; limit: number }) =>
      getDeductionTypesUseCase.execute(
        new GetMasterDataDeductionTypesDTO({
          search: args.search,
          child_search: args.search,
          page_number: args.page,
          page_size: args.limit,
        }),
      ),
    [getDeductionTypesUseCase],
  );

  const {
    items: deductionTypes,
    loading,
    error,
    search,
    setSearch,
    page,
    setPage,
    limit,
    total,
    refetch,
  } = useMasterDataListQuery({
    brandKey,
    defaultPageSize,
    errorMessage: "Failed to load master data deduction types",
    cacheScope: "deduction-types",
    fetchPage,
  });

  const value = useMemo(
    (): MasterDataDeductionTypesDataContextValue => ({
      brandKey,
      deductionTypes,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    }),
    [
      brandKey,
      deductionTypes,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    ],
  );

  return (
    <MasterDataDeductionTypesDataContext.Provider value={value}>
      {children}
    </MasterDataDeductionTypesDataContext.Provider>
  );
}
